import cx from 'classnames';
import React, {memo} from 'react';
import {createPortal} from 'react-dom';
import {useSelector} from 'react-redux';
import {ExitPremiumPreviewModal} from '~/common/FabricInvitation/EventCreateDesign/components/ExitPremiumPreviewModal/ExitPremiumPreviewModal';
import {
  selectIsPremium,
  selectIsPremiumTemplate,
} from '~/common/FabricInvitation/reducers/fabricTemplate/selectors';
import {eventInEditMode} from '~/common/fabric/reducers/details/selectors';
import {selectIsUpsellConfirmed} from '~/common/fabric/reducers/modals';
import {UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {UPSELL_MODES} from '~/common/fabric/UpsellModal/UpsellModalContent';
import {shouldUseNamedStepsNav} from '~/common/utils/event_utils';
import {Button} from '~library/atoms/Button';
import {LoadingOverlay} from '~library/molecules/LoadingOverlay';
import {useNavBottomState} from './hooks/useNavBottomState';

import './NavBottom.sass';
import {useCurrentRoute} from '~/CreateFabricInvitation/navigation';

interface NavBottomProps {
  allBackgroundsLoaded: boolean;
  currentRoute: {
    nextButtonText: string;
  };
}

const NavBottomComponent: React.FC<NavBottomProps> = ({allBackgroundsLoaded, currentRoute}) => {
  const {showUpsellModal, showExitPremiumPreviewModal, savingStep, handleNavigation, handleModals} =
    useNavBottomState();

  const isPremium = useSelector(selectIsPremium);
  const isPremiumTemplate = useSelector(selectIsPremiumTemplate);
  const upsellConfirmed = useSelector(selectIsUpsellConfirmed);
  const inEditMode = useSelector(eventInEditMode);

  const buttonText =
    inEditMode && !isPremiumTemplate && isPremium && upsellConfirmed
      ? 'Next: Checkout'
      : currentRoute.nextButtonText;

  // Loading overlay portal
  const renderLoadingPortal = () => {
    const fabricRoot = document.getElementById('fabric-create-invitation-root');
    if (!fabricRoot || !savingStep) return null;
    return createPortal(
      <LoadingOverlay loadingMessage="Saving..." show={savingStep} />,
      fabricRoot
    );
  };

  const route = useCurrentRoute();
  const isStep1 = route?.name === 'customize' && !route?.meta?.editing;

  // Modals portal
  const renderModals = () => {
    const modalRoot = document.getElementById('pbModalRoot');
    if (!modalRoot) return null;

    return (
      <>
        {showUpsellModal && (
          <UpsellModal
            noAutoRestore
            toggleUpsellModal={handleModals.toggleUpsellModal}
            closeOverlay={handleModals.handleCloseUpsell}
            onConfirmAction={handleModals.upgradeAndContinue}
            mode={isStep1 ? UPSELL_MODES.STEP_1_COPY : UPSELL_MODES.UNLOCK_DESIGN_TOOLS}
          />
        )}

        {showExitPremiumPreviewModal &&
          createPortal(
            <ExitPremiumPreviewModal
              closeOverlay={handleModals.handleCloseExitPreview}
              onConfirmAction={handleModals.downgradeBackToBasic}
            />,
            modalRoot
          )}
      </>
    );
  };

  const useNamedStepsNav = shouldUseNamedStepsNav();
  if (useNamedStepsNav && !inEditMode) return null;

  return (
    <div className={cx('create-design__nav-bottom')}>
      <Button
        id="navBottom"
        className="create-design__nav-bottom__next"
        data-qa-id="nav-bottom-next"
        variant="primary"
        size="large"
        disabled={!allBackgroundsLoaded}
        onClick={handleNavigation}
      >
        {buttonText}
      </Button>

      {renderModals()}
      {renderLoadingPortal()}
    </div>
  );
};

export const NavBottom = memo(NavBottomComponent);
